/**
 * PromptStore — CRUD for resume-prompts collection.
 *
 * Each prompt doc:
 * {
 *   name, description, promptText, scaleDescription: { '1-2': '...', '3-4': '...', ... },
 *   createdAt, createdBy, updatedAt
 * }
 */

/** Default scale thresholds shared by all scoring prompts */
export const DEFAULT_SCALE = {
    '1-2': 'No demonstrated capability or relevance',
    '3-4': 'Basic/entry-level with limited evidence',
    '5-6': 'Moderate proficiency, some relevant experience',
    '7-8': 'Strong capability with clear evidence',
    '9-10': 'Exceptional/expert-level, extensive evidence',
};

export class PromptStore {
    constructor(db) {
        this._db = db;
        this._collection = db.collection('resume-prompts');
        this._unsub = null;
        this._prompts = [];
        this._listeners = [];
    }

    subscribeAll() {
        this._unsub = this._collection
            .orderBy('createdAt', 'asc')
            .onSnapshot(snap => {
                this._prompts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                this._notify();
            });
    }

    onChange(fn) { this._listeners.push(fn); }

    getAll() { return this._prompts; }

    getById(id) { return this._prompts.find(p => p.id === id) || null; }

    async create(data, createdBy) {
        const ref = await this._collection.add({
            name: data.name || 'Untitled',
            description: data.description || '',
            promptText: data.promptText || '',
            scaleDescription: data.scaleDescription || { ...DEFAULT_SCALE },
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            createdBy,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return ref.id;
    }

    async update(id, fields) {
        await this._collection.doc(id).update({
            ...fields,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    }

    async remove(id) {
        await this._collection.doc(id).delete();
    }

    dispose() {
        if (this._unsub) this._unsub();
    }

    _notify() {
        for (const fn of this._listeners) fn(this._prompts);
    }
}
